import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faFile, faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import useAgencyOrderBundles from '@hooks/useAgencyOrderBundles'
import useAgencySelector from '@hooks/useAgencySelector'
import { generateUrls } from '@hooks/useOrderBundle'

import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import OrderBundleListItem from '../OrderBundleListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'order_bundle_string',
      label: 'Filter by Title',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
      },
      key: 'publication_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providerPriceLists',
      },
      key: 'provider_price_list_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  useLocation()

  const {
    hidden = 'false',
  } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden']) as { hidden: 'true' | 'false' }

  const { selectedAgency, selectedAgencyId } = useAgencySelector()

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const useAgencyOrderBundlePayload = useAgencyOrderBundles({
    filters: {
      ...pageFilters,
      agency_id: selectedAgencyId,
      hidden,
    },
    performHttpRequests: !!selectedAgency?.id,
    requestOptions: {
      include_agency_order_bundle_order_bundle: true,
      include_agency_order_bundle_price_list_ids: !!pageFilters.provider_price_list_id,
      include_agency_order_bundle_publication_ids: !!pageFilters.publication_id,
    },
    sortKey: 'order_bundle_id',
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyOrderBundles,
    filteredAgencyOrderBundlesCount,
    hasFilteredAgencyOrderBundles,
    loading,
  } = useAgencyOrderBundlePayload

  const { hiddenOrderBundlesIndexUrl, orderBundlesIndexUrl } = generateUrls({}, {
    prefixRoute: `agencies/${selectedAgencyId}/manage`,
  })

  return (
    <>
      <Helmet>
        <title>Bundles | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey={hidden}
        nestedNavigation
        tabBarItems={[
          {
            href: orderBundlesIndexUrl,
            icon: faHome,
            key: 'false',
            title: 'Current',
          },
          {
            href: hiddenOrderBundlesIndexUrl,
            icon: faFile,
            key: 'true',
            title: 'Hidden',
          },
        ]}
        title="Bundles"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.orderBundles.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAgencyOrderBundles && <ListBlankState />}

            {!loading && hasFilteredAgencyOrderBundles && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Bundles">
                <Box flexDirection="column">
                  {filteredAgencyOrderBundles.map(agencyOrderBundle => (
                    <OrderBundleListItem agencyOrderBundle={agencyOrderBundle} key={agencyOrderBundle.id} />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredAgencyOrderBundlesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.orderBundles.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
